import { useTranslation } from 'react-i18next';
import { wageringTargetMinor } from '@/features/promo/reward-rules.js';
import { Button } from '@/components/ui/button.js';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog.js';
import { Money } from '@/components/money/Money.js';
import { useFormatMoney } from '@/components/money/MoneyProvider.js';
import { prizeLabel } from '@/features/spin/prize-label.js';
import type { SpinResult } from '@/features/spin/api.js';

interface Props {
  open: boolean;
  onClose: () => void;
  result: SpinResult | null;
  currency: string;
  turnoverMultiplier: number;
  turnoverRequired?: number | null;
}

export function SpinResultModal({ open, onClose, result, currency, turnoverMultiplier, turnoverRequired }: Props) {
  const { t } = useTranslation('spin');
  const formatMoney = useFormatMoney();
  const cash = result?.cashAwarded ?? 0;
  const won = cash > 0;
  // `spin_results.prize_value` holds the whole prize row, so the operator's
  // artwork (#300) arrives with the draw — no second request for it.
  const prize = result?.prizeValue;
  // Authoritative value from /draw when present. The fallback used to be
  // `Math.floor(cash * turnoverMultiplier)` — float multiplication on money
  // minor units, which CLAUDE.md 2.2 forbids by name and which is the same class
  // as the fix shipped one directory over in this SPA (W8). It is now the same
  // integer expression as the promo tooltip and the promo card, which is also
  // the one the server enforces.
  const turnover =
    turnoverRequired ?? Number(wageringTargetMinor(BigInt(Math.trunc(cash)), turnoverMultiplier));
  return (
    <Dialog
      open={open && !!result}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-sm text-center">
        <DialogTitle>{t('result.title')}</DialogTitle>
        {prize?.imageUrl && (
          <img
            src={prize.imageUrl}
            alt={prizeLabel(prize, t)}
            className="mx-auto h-24 w-24 rounded-xl object-cover"
          />
        )}
        {won ? (
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] text-[var(--text-secondary)]">{t('result.bonus')}</span>
            <div className="text-[var(--success)] font-extrabold text-2xl">
              <Money minor={cash} currency={currency} />
            </div>
            <p className="text-[12px] text-[var(--text-muted)]">
              {t('result.turnover', { amount: formatMoney(turnover, currency) })}
            </p>
          </div>
        ) : (
          <div className="text-[var(--text-muted)] text-[15px]">{t('result.nothing')}</div>
        )}
        <Button fullWidth glow onClick={onClose}>
          {t('result.close')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
